import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../module_roots/AuthContext';
import "../styles/SupplierOrder.css";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faShoppingCart,
    faUser,
    faPhone,
    faEnvelope,
    faMapMarkerAlt,
    faCalendarAlt,
    faFileInvoice,
    faBox,
    faRupeeSign,
    faTag,
    faBalanceScale,
    faDollarSign,
    faCalculator,
    faExclamationTriangle,
    faPlus,
    faMinus,
    faTrash,
    faSave,
    faTimes,
    faSpinner,
    faCheckCircle,
    faExclamationCircle,
    faSearch,
    faUpload,
    faReceipt,
    faChevronDown,
    faChevronUp,
    faBoxes,
    faBarcode,
    faBuilding,
    faGlobe,
    faStickyNote,
    faClock,
    faTruck,
    faCreditCard,
    faMoneyBillWave,
    faPercentage,
    faIndustry
} from '@fortawesome/free-solid-svg-icons';

// Create translator instance
const createSupplierOrderTranslator = () => {
    let cache = new Map();
    let preloadedLanguages = new Set(['en', 'ur', 'ar', 'es']);
    let translationListeners = new Set();

    const loadFromCache = () => {
        try {
            const cached = localStorage.getItem('ourzest_supplierorder_translations');
            if (cached) {
                const parsed = JSON.parse(cached);
                Object.entries(parsed).forEach(([key, value]) => {
                    cache.set(key, value);
                });
            }
        } catch (e) {
            console.log("No cache found in Supplier Order Form");
        }
    };

    const saveToCache = () => {
        try {
            const cacheObj = {};
            cache.forEach((value, key) => {
                cacheObj[key] = value;
            });
            localStorage.setItem('ourzest_supplierorder_translations', JSON.stringify(cacheObj));
        } catch (e) {
            console.error("Error saving to cache:", e);
        }
    };

    const translateAPI = async (text, targetLang) => {
        if (targetLang === 'en') return text;

        try {
            const response = await fetch('https://translate.argosopentech.com/translate', {
                method: 'POST',
                body: JSON.stringify({ q: text, source: 'en', target: targetLang }),
                headers: { 'Content-Type': 'application/json' }
            });

            if (response.ok) {
                const data = await response.json();
                return data.translatedText;
            }
        } catch (e) {
            console.log("LibreTranslate failed, trying MyMemory");
        }

        try {
            const response = await fetch(
                `https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=en|${targetLang}`
            );
            if (response.ok) {
                const data = await response.json();
                return data.responseData.translatedText;
            }
        } catch (e) {
            console.log("MyMemory translation failed");
        }

        return text;
    };

    const translateText = async (text, targetLang) => {
        const cacheKey = `${text}_${targetLang}`;

        if (cache.has(cacheKey)) {
            return cache.get(cacheKey);
        }

        const translated = await translateAPI(text, targetLang);
        cache.set(cacheKey, translated);
        saveToCache();

        return translated;
    };

    const defaultTexts = {
        addOrder: 'Add Supplier Order',
        editOrder: 'Edit Supplier Order',
        orderDetails: 'Order Details',
        orderNumber: 'Order Number',
        supplier: 'Supplier',
        selectSupplier: 'Select supplier',
        searchSupplier: 'Search supplier...',
        supplierName: 'Supplier Name',
        phone: 'Phone',
        email: 'Email',
        address: 'Address',
        company: 'Company',
        date: 'Order Date',
        expectedDelivery: 'Expected Delivery',
        orderFile: 'Order File (Optional)',
        chooseFile: 'Choose file',
        noFile: 'No file chosen',
        items: 'Order Items',
        addItem: 'Add Item',
        productName: 'Product Name',
        productColorCode: 'Color Code',
        batchNumber: 'Batch Number',
        barcode: 'Barcode',
        unit: 'Unit',
        price: 'Price',
        quantity: 'Quantity',
        total: 'Total',
        remove: 'Remove',
        calculations: 'Calculations',
        subtotal: 'Subtotal',
        tax: 'Tax',
        discount: 'Discount',
        grandTotal: 'Grand Total',
        paidAmount: 'Paid Amount',
        balance: 'Balance',
        paymentStatus: 'Payment Status',
        paymentMethod: 'Payment Method',
        orderStatus: 'Order Status',
        notes: 'Notes',
        saveOrder: 'Save Order',
        updateOrder: 'Update Order',
        clear: 'Clear',
        saving: 'Saving...',
        orderSaved: 'Order saved successfully!',
        orderUpdated: 'Order updated successfully!',
        failedSave: 'Failed to save order',
        failedUpdate: 'Failed to update order',
        networkError: 'Network error occurred',
        orderRequired: 'Order number is required',
        supplierRequired: 'Supplier is required',
        dateRequired: 'Date is required',
        itemsRequired: 'At least one item is required',
        productRequired: 'Product name is required',
        priceRequired: 'Price is required',
        quantityRequired: 'Quantity is required',
        invalidPrice: 'Please enter a valid price',
        invalidQuantity: 'Please enter a valid quantity',
        invalidPaid: 'Please enter a valid paid amount',
        success: 'Success',
        error: 'Error',
        warning: 'Warning',
        piece: 'Piece',
        box: 'Box',
        packet: 'Packet',
        kg: 'Kilogram',
        gram: 'Gram',
        liter: 'Liter',
        ml: 'Milliliter',
        dozen: 'Dozen',
        meter: 'Meter',
        yard: 'Yard',
        roll: 'Roll',
        selectUnit: 'Select unit',
        browse: 'Browse',
        generate: 'Generate',
        optional: 'Optional',
        searchProduct: 'Search product...',
        searchFabricProduct: 'Search fabric product...',
        noProductsFound: 'No products found',
        noFabProductsFound: 'No fabric products found',
        noSuppliersFound: 'No suppliers found',
        pending: 'Pending',
        processing: 'Processing',
        completed: 'Completed',
        cancelled: 'Cancelled',
        paid: 'Paid',
        unpaid: 'Unpaid',
        partial: 'Partial',
        cash: 'Cash',
        card: 'Card',
        bankTransfer: 'Bank Transfer',
        cheque: 'Cheque',
        credit: 'Credit',
        selectPaymentStatus: 'Select payment status',
        selectOrderStatus: 'Select order status',
        selectPaymentMethod: 'Select payment method',
        fabricProducts: 'Fabric Products',
        regularProducts: 'Regular Products',
        productType: 'Product Type',
        switchToFabric: 'Switch to Fabric Products',
        switchToRegular: 'Switch to Regular Products',
        failedLoadSuppliers: 'Failed to load suppliers',
        failedLoadProducts: 'Failed to load products',
        networkErrorLoad: 'Network error loading'
    };

    const getTranslations = async (lang) => {
        if (lang === 'en') {
            return defaultTexts;
        }

        const translations = {};
        const promises = [];

        Object.keys(defaultTexts).forEach(key => {
            const cacheKey = `${defaultTexts[key]}_${lang}`;
            if (cache.has(cacheKey)) {
                translations[key] = cache.get(cacheKey);
            } else {
                promises.push(
                    translateText(defaultTexts[key], lang).then(translated => {
                        translations[key] = translated;
                    })
                );
            }
        });

        if (promises.length > 0) {
            await Promise.all(promises);
            saveToCache();
        }

        return translations;
    };

    const addListener = (callback) => {
        translationListeners.add(callback);
    };

    const removeListener = (callback) => {
        translationListeners.delete(callback);
    };

    const notifyListeners = (lang) => {
        translationListeners.forEach(callback => callback(lang));
    };

    const checkLanguageChange = () => {
        const currentLang = localStorage.getItem('ourzest_language') || 'en';
        notifyListeners(currentLang);
    };

    const startLanguageCheck = () => {
        checkLanguageChange();
        const intervalId = setInterval(checkLanguageChange, 500);
        window.addEventListener('storage', checkLanguageChange);
        return () => {
            clearInterval(intervalId);
            window.removeEventListener('storage', checkLanguageChange);
        };
    };

    loadFromCache();

    return {
        getTranslations,
        translateText,
        addListener,
        removeListener,
        startLanguageCheck
    };
};

const supplierOrderTranslator = createSupplierOrderTranslator();

// Toast Notification Component
const ToastNotification = ({ toast, setToast, translations }) => {
    const getIconForType = (type) => {
        switch (type) {
            case 'success':
                return faCheckCircle;
            case 'error':
                return faExclamationCircle;
            case 'warning':
                return faExclamationTriangle;
            default:
                return faCheckCircle;
        }
    };

    const getTitleForType = (type) => {
        switch (type) {
            case 'success':
                return translations?.success || 'Success';
            case 'error':
                return translations?.error || 'Error';
            case 'warning':
                return translations?.warning || 'Warning';
            default:
                return '';
        }
    };

    if (!toast.show) return null;

    const opacity = Math.max(toast.progress / 100, 0.1);

    return (
        <div
            className={`toast-notification ${toast.type} ${toast.direction === 'rtl' ? 'toast-rtl' : 'toast-ltr'}`}
            style={{ opacity }}
        >
            <div className="toast-content">
                <div className="toast-icon">
                    <FontAwesomeIcon icon={getIconForType(toast.type)} />
                </div>
                <div className="toast-text">
                    <div className="toast-title">
                        {getTitleForType(toast.type)}
                    </div>
                    <div className="toast-message">
                        {toast.message}
                    </div>
                </div>
                <button
                    className="toast-close"
                    onClick={() => setToast(prev => ({ ...prev, show: false }))}
                >
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
            <div className="toast-timer">
                <div
                    className="toast-progress-bar"
                    style={{
                        width: `${toast.progress}%`,
                        transition: 'width 0.1s linear'
                    }}
                ></div>
                <div className="toast-countdown">
                    {toast.timeLeft}s
                </div>
            </div>
        </div>
    );
};

// Items Table Component with Product Search (Supports both regular and fabric products)
const OrderItemsTable = ({
    items,
    onItemChange,
    onRemove,
    onAdd,
    errors,
    disabled,
    translations,
    isRTL,
    user,
    shopId,
    showToast,
    productType = 'regular' // 'regular' or 'fabric'
}) => {
    const tableRef = useRef(null);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [products, setProducts] = useState([]);
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [productSearchTerms, setProductSearchTerms] = useState({});
    const [showProductDropdown, setShowProductDropdown] = useState({});
    const [loadingProducts, setLoadingProducts] = useState(false);
    const productSearchRefs = useRef({});

    // Local items state to prevent overwriting
    const [localItems, setLocalItems] = useState([]);
    const initializedRef = useRef(false);

    // Initialize local items
    useEffect(() => {
        if (!initializedRef.current && items && items.length > 0) {
            setLocalItems(items);
            initializedRef.current = true;
        }
    }, [items]);

    // Update local items when parent items change length
    const itemsRef = useRef(items);
    useEffect(() => {
        if (itemsRef.current.length !== items.length) {
            setLocalItems(items);
            itemsRef.current = items;
        }
    }, [items]);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Load products based on product type
    const loadProducts = async () => {
        if (!user?.username || !shopId) return;

        setLoadingProducts(true);
        try {
            // Determine which endpoint to use based on product type
            const endpoint = productType === 'fabric' 
                ? 'https://ourzestapp.online/oz_backend/get_fab_products.php' 
                : 'https://ourzestapp.online/oz_backend/get_products.php';

            const response = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    username: user?.username,
                    shop_id: shopId,
                    searchTerm: '',
                    page: 1,
                    limit: 50
                })
            });

            const data = await response.json();

            if (data.status === 'success') {
                setProducts(data.data);
                console.log(`${productType} products loaded:`, data.data.length);
            } else {
                console.error(`Error loading ${productType} products:`, data.message);
                if (showToast) {
                    showToast('error', data.message || `${translations?.failedLoadProducts || 'Failed to load products'}`);
                }
            }
        } catch (error) {
            console.error(`Error loading ${productType} products:`, error);
            if (showToast) {
                showToast('error', `${translations?.networkErrorLoad || 'Network error loading'} ${productType} products`);
            }
        } finally {
            setLoadingProducts(false);
        }
    };

    // Load products on mount and when product type changes
    useEffect(() => {
        loadProducts();
    }, [user?.username, shopId, productType]);

    // Handle click outside product dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            Object.keys(productSearchRefs.current).forEach(index => {
                if (productSearchRefs.current[index] && !productSearchRefs.current[index].contains(event.target)) {
                    setShowProductDropdown(prev => ({ ...prev, [index]: false }));
                }
            });
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleProductSearch = (index, term) => {
        setProductSearchTerms(prev => ({ ...prev, [index]: term }));

        if (term.trim() === '') {
            setFilteredProducts([]);
        } else {
            const filtered = products.filter(product =>
                product.product_name?.toLowerCase().includes(term.toLowerCase()) ||
                product.batch_no?.toLowerCase().includes(term.toLowerCase()) ||
                product.barcode?.toLowerCase().includes(term.toLowerCase()) ||
                (product.product_colorcode && product.product_colorcode.toLowerCase().includes(term.toLowerCase()))
            );
            setFilteredProducts(filtered);
        }
        setShowProductDropdown(prev => ({ ...prev, [index]: true }));
    };

    // Select product and auto-fill details
    const selectProduct = (index, product) => {
        console.log('=== SELECTING PRODUCT ===');
        console.log('Product:', product);
        
        // Get all product details
        const productName = product.product_name || '';
        const batchNo = product.batch_no || '';
        const productColorcode = product.product_colorcode || '';
        const barcode = product.barcode || '';
        const unit = product.unit || '';
        const price = product.cost_price || product.sell_price || '';
        
        // Update parent state
        onItemChange(index, 'product_name', productName);
        onItemChange(index, 'product_id', product.id);
        onItemChange(index, 'batch_no', batchNo);
        onItemChange(index, 'product_colorcode', productColorcode);
        onItemChange(index, 'barcode', barcode);
        onItemChange(index, 'unit', unit);
        onItemChange(index, 'price', price);

        // Update local items
        setLocalItems(prevItems => {
            const updated = [...prevItems];
            if (!updated[index]) {
                updated[index] = {};
            }
            updated[index] = {
                ...updated[index],
                product_name: productName,
                product_id: product.id,
                batch_no: batchNo,
                product_colorcode: productColorcode,
                barcode: barcode,
                unit: unit,
                price: price
            };
            return updated;
        });

        // Clear search and dropdown
        setProductSearchTerms(prev => ({ ...prev, [index]: productName }));
        setShowProductDropdown(prev => ({ ...prev, [index]: false }));

        // Show success message
        if (showToast) {
            showToast('success', `Product "${productName}" selected`);
        }
    };

    const units = [
        { value: 'piece', label: translations?.piece || 'Piece' },
        { value: 'box', label: translations?.box || 'Box' },
        { value: 'packet', label: translations?.packet || 'Packet' },
        { value: 'kg', label: translations?.kg || 'Kilogram' },
        { value: 'gram', label: translations?.gram || 'Gram' },
        { value: 'liter', label: translations?.liter || 'Liter' },
        { value: 'ml', label: translations?.ml || 'Milliliter' },
        { value: 'dozen', label: translations?.dozen || 'Dozen' },
        { value: 'meter', label: translations?.meter || 'Meter' },
        { value: 'yard', label: translations?.yard || 'Yard' },
        { value: 'roll', label: translations?.roll || 'Roll' }
    ];

    const calculateTotal = (price, quantity) => {
        const p = parseFloat(price) || 0;
        const q = parseFloat(quantity) || 0;
        return (p * q).toFixed(2);
    };

    // Display items
    const displayItems = localItems.length > 0 ? localItems : items;

    // Mobile view
    if (isMobile) {
        return (
            <div className="items-mobile-container">
                <div className="items-header-mobile">
                    <h4>
                        <FontAwesomeIcon icon={faBoxes} />
                        {productType === 'fabric' 
                            ? (translations?.fabricProducts || 'Fabric Products')
                            : (translations?.items || 'Items')}
                    </h4>
                    <button
                        type="button"
                        className="btn-add-item-mobile"
                        onClick={() => {
                            onAdd();
                            setLocalItems(prev => [...prev, {
                                product_name: '',
                                product_id: '',
                                batch_no: '',
                                product_colorcode: '',
                                barcode: '',
                                unit: '',
                                price: '',
                                quantity: ''
                            }]);
                        }}
                        disabled={disabled}
                    >
                        <FontAwesomeIcon icon={faPlus} />
                        <span>{translations?.addItem || 'Add Item'}</span>
                    </button>
                </div>
                <div className="items-cards-container">
                    {displayItems.map((item, index) => (
                        <div key={index} className="item-card-mobile">
                            <div className="item-card-header">
                                <span className="item-number">
                                    <FontAwesomeIcon icon={faBox} className="header-icon-only" />
                                    Item {index + 1}
                                </span>
                                <button
                                    type="button"
                                    className="item-remove-btn-mobile"
                                    onClick={() => {
                                        onRemove(index);
                                        setLocalItems(prev => prev.filter((_, i) => i !== index));
                                    }}
                                    disabled={disabled || displayItems.length === 1}
                                >
                                    <FontAwesomeIcon icon={faTrash} />
                                </button>
                            </div>

                            <div className="item-card-content">
                                {/* Product Name with Search */}
                                <div
                                    className={`mobile-field ${errors[`product_name_${index}`] ? 'has-error' : ''}`}
                                    ref={el => productSearchRefs.current[index] = el}
                                >
                                    <label>
                                        <FontAwesomeIcon icon={faSearch} className="field-icon" />
                                        {translations?.productName || 'Product Name'} *
                                    </label>
                                    <div className="product-search-container">
                                        <input
                                            type="text"
                                            value={productSearchTerms[index] !== undefined ? productSearchTerms[index] : item.product_name}
                                            onChange={(e) => handleProductSearch(index, e.target.value)}
                                            onFocus={() => setShowProductDropdown(prev => ({ ...prev, [index]: true }))}
                                            placeholder={productType === 'fabric' 
                                                ? (translations?.searchFabricProduct || 'Search fabric product...')
                                                : (translations?.searchProduct || 'Search product...')}
                                            disabled={disabled}
                                            dir={isRTL ? 'rtl' : 'ltr'}
                                            autoComplete="off"
                                        />
                                        {loadingProducts && (
                                            <div className="search-loading">
                                                <FontAwesomeIcon icon={faSpinner} spin />
                                            </div>
                                        )}

                                        {/* Product Dropdown */}
                                        {showProductDropdown[index] && filteredProducts.length > 0 && (
                                            <div className="product-dropdown">
                                                {filteredProducts.map(product => (
                                                    <div
                                                        key={product.id}
                                                        className="product-option"
                                                        onClick={() => selectProduct(index, product)}
                                                    >
                                                        <div className="product-name">{product.product_name}</div>
                                                        <div className="product-details">
                                                            {product.product_colorcode && (
                                                                <span><FontAwesomeIcon icon={faTag} /> Color: {product.product_colorcode}</span>
                                                            )}
                                                            {product.batch_no && (
                                                                <span><FontAwesomeIcon icon={faTag} /> Batch: {product.batch_no}</span>
                                                            )}
                                                            {product.barcode && (
                                                                <span><FontAwesomeIcon icon={faBarcode} /> {product.barcode}</span>
                                                            )}
                                                            {product.unit && (
                                                                <span>Unit: {product.unit}</span>
                                                            )}
                                                            {(product.cost_price || product.sell_price) && (
                                                                <span><FontAwesomeIcon icon={faRupeeSign} /> {product.cost_price || product.sell_price}</span>
                                                            )}
                                                        </div>
                                                        <div className="product-stock">
                                                            Stock: {product.quantity} {product.unit}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {showProductDropdown[index] && filteredProducts.length === 0 && productSearchTerms[index] && (
                                            <div className="product-dropdown empty">
                                                <FontAwesomeIcon icon={faExclamationTriangle} />
                                                {productType === 'fabric'
                                                    ? (translations?.noFabProductsFound || 'No fabric products found')
                                                    : (translations?.noProductsFound || 'No products found')}
                                            </div>
                                        )}
                                    </div>
                                    {errors[`product_name_${index}`] && (
                                        <div className="error-text">
                                            <FontAwesomeIcon icon={faExclamationTriangle} />
                                            {errors[`product_name_${index}`]}
                                        </div>
                                    )}
                                </div>

                                {/* Color Code (for fabric products) */}
                                {productType === 'fabric' && (
                                    <div className="mobile-field">
                                        <label>{translations?.productColorCode || 'Color Code'}</label>
                                        <input
                                            type="text"
                                            value={item.product_colorcode || ''}
                                            onChange={(e) => {
                                                const newValue = e.target.value;
                                                onItemChange(index, 'product_colorcode', newValue);
                                                setLocalItems(prev => {
                                                    const updated = [...prev];
                                                    if (!updated[index]) updated[index] = {};
                                                    updated[index] = { ...updated[index], product_colorcode: newValue };
                                                    return updated;
                                                });
                                            }}
                                            placeholder="Color code"
                                            disabled={disabled}
                                            dir={isRTL ? 'rtl' : 'ltr'}
                                        />
                                    </div>
                                )}

                                {/* Batch Number */}
                                <div className={`mobile-field ${errors[`batch_no_${index}`] ? 'has-error' : ''}`}>
                                    <label>{translations?.batchNumber || 'Batch Number'}</label>
                                    <input
                                        type="text"
                                        value={item.batch_no || ''}
                                        onChange={(e) => {
                                            const newValue = e.target.value;
                                            onItemChange(index, 'batch_no', newValue);
                                            setLocalItems(prev => {
                                                const updated = [...prev];
                                                if (!updated[index]) updated[index] = {};
                                                updated[index] = { ...updated[index], batch_no: newValue };
                                                return updated;
                                            });
                                        }}
                                        placeholder={translations?.batchNumber || 'Batch number'}
                                        disabled={disabled}
                                        dir={isRTL ? 'rtl' : 'ltr'}
                                    />
                                    {errors[`batch_no_${index}`] && (
                                        <div className="error-text">
                                            {errors[`batch_no_${index}`]}
                                        </div>
                                    )}
                                </div>

                                {/* Barcode */}
                                <div className="mobile-field">
                                    <label>{translations?.barcode || 'Barcode'}</label>
                                    <input
                                        type="text"
                                        value={item.barcode || ''}
                                        onChange={(e) => {
                                            const newValue = e.target.value;
                                            onItemChange(index, 'barcode', newValue);
                                            setLocalItems(prev => {
                                                const updated = [...prev];
                                                if (!updated[index]) updated[index] = {};
                                                updated[index] = { ...updated[index], barcode: newValue };
                                                return updated;
                                            });
                                        }}
                                        placeholder="Barcode"
                                        disabled={disabled}
                                        dir={isRTL ? 'rtl' : 'ltr'}
                                    />
                                </div>

                                {/* Unit */}
                                <div className={`mobile-field ${errors[`unit_${index}`] ? 'has-error' : ''}`}>
                                    <label>{translations?.unit || 'Unit'} *</label>
                                    <select
                                        value={item.unit}
                                        onChange={(e) => {
                                            const newValue = e.target.value;
                                            onItemChange(index, 'unit', newValue);
                                            setLocalItems(prev => {
                                                const updated = [...prev];
                                                if (!updated[index]) updated[index] = {};
                                                updated[index] = { ...updated[index], unit: newValue };
                                                return updated;
                                            });
                                        }}
                                        disabled={disabled}
                                        dir={isRTL ? 'rtl' : 'ltr'}
                                    >
                                        <option value="">{translations?.selectUnit || 'Select'}</option>
                                        {units.map((unit) => (
                                            <option key={unit.value} value={unit.value}>
                                                {unit.label}
                                            </option>
                                        ))}
                                    </select>
                                    {errors[`unit_${index}`] && (
                                        <div className="error-text">
                                            {errors[`unit_${index}`]}
                                        </div>
                                    )}
                                </div>

                                {/* Price */}
                                <div className={`mobile-field ${errors[`price_${index}`] ? 'has-error' : ''}`}>
                                    <label>{translations?.price || 'Price'} *</label>
                                    <input
                                        type="number"
                                        value={item.price}
                                        onChange={(e) => {
                                            const newValue = e.target.value;
                                            onItemChange(index, 'price', newValue);
                                            setLocalItems(prev => {
                                                const updated = [...prev];
                                                if (!updated[index]) updated[index] = {};
                                                updated[index] = { ...updated[index], price: newValue };
                                                return updated;
                                            });
                                        }}
                                        step="0.01"
                                        min="0"
                                        placeholder="0.00"
                                        disabled={disabled}
                                        dir="ltr"
                                    />
                                    {errors[`price_${index}`] && (
                                        <div className="error-text">
                                            {errors[`price_${index}`]}
                                        </div>
                                    )}
                                </div>

                                {/* Quantity */}
                                <div className={`mobile-field ${errors[`quantity_${index}`] ? 'has-error' : ''}`}>
                                    <label>{translations?.quantity || 'Quantity'} *</label>
                                    <input
                                        type="number"
                                        value={item.quantity}
                                        onChange={(e) => {
                                            const newValue = e.target.value;
                                            onItemChange(index, 'quantity', newValue);
                                            setLocalItems(prev => {
                                                const updated = [...prev];
                                                if (!updated[index]) updated[index] = {};
                                                updated[index] = { ...updated[index], quantity: newValue };
                                                return updated;
                                            });
                                        }}
                                        step="0.001"
                                        min="0.001"
                                        placeholder="0.000"
                                        disabled={disabled}
                                        dir="ltr"
                                    />
                                    {errors[`quantity_${index}`] && (
                                        <div className="error-text">
                                            {errors[`quantity_${index}`]}
                                        </div>
                                    )}
                                </div>

                                {/* Total */}
                                <div className="mobile-field">
                                    <label>{translations?.total || 'Total'}</label>
                                    <input
                                        type="text"
                                        value={calculateTotal(item.price, item.quantity)}
                                        readOnly
                                        disabled
                                        className="readonly-input"
                                    />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    // Desktop view
    return (
        <div className="items-table-container">
            <div className="table-header-actions">
                <h4>
                    <FontAwesomeIcon icon={faBoxes} className="header-icon-only" />
                    {productType === 'fabric' 
                        ? (translations?.fabricProducts || 'Fabric Products')
                        : (translations?.items || 'Items')}
                </h4>
                <button
                    type="button"
                    className="btn-add-item-table"
                    onClick={() => {
                        onAdd();
                        setLocalItems(prev => [...prev, {
                            product_name: '',
                            product_id: '',
                            batch_no: '',
                            product_colorcode: '',
                            barcode: '',
                            unit: '',
                            price: '',
                            quantity: ''
                        }]);
                    }}
                    disabled={disabled}
                >
                    <FontAwesomeIcon icon={faPlus} />
                    <span>{translations?.addItem || 'Add Item'}</span>
                </button>
            </div>
            <div className="table-wrapper" ref={tableRef}>
                <table className="items-table">
                    <thead>
                        <tr>
                            <th className="sticky-col product-col">
                                {translations?.productName || 'Product Name'} *
                            </th>
                            {productType === 'fabric' && (
                                <th className="sticky-col colorcode-col">
                                    {translations?.productColorCode || 'Color Code'}
                                </th>
                            )}
                            <th className="sticky-col batch-col">
                                {translations?.batchNumber || 'Batch No'}
                            </th>
                            <th className="sticky-col barcode-col">
                                {translations?.barcode || 'Barcode'}
                            </th>
                            <th className="sticky-col unit-col">
                                {translations?.unit || 'Unit'} *
                            </th>
                            <th className="sticky-col price-col">
                                {translations?.price || 'Price'} *
                            </th>
                            <th className="sticky-col qty-col">
                                {translations?.quantity || 'Quantity'} *
                            </th>
                            <th className="sticky-col total-col">
                                {translations?.total || 'Total'}
                            </th>
                            <th className="sticky-col action-col"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {displayItems.map((item, index) => (
                            <tr key={index} className={errors[`product_name_${index}`] ? 'error-row' : ''}>
                                <td>
                                    <div
                                        className="table-field product-search-field"
                                        ref={el => productSearchRefs.current[index] = el}
                                    >
                                        <div className="product-search-container">
                                            <input
                                                type="text"
                                                value={productSearchTerms[index] !== undefined ? productSearchTerms[index] : item.product_name}
                                                onChange={(e) => handleProductSearch(index, e.target.value)}
                                                onFocus={() => setShowProductDropdown(prev => ({ ...prev, [index]: true }))}
                                                placeholder={productType === 'fabric' 
                                                    ? (translations?.searchFabricProduct || 'Search fabric product...')
                                                    : (translations?.searchProduct || 'Search product...')}
                                                disabled={disabled}
                                                dir={isRTL ? 'rtl' : 'ltr'}
                                                className={errors[`product_name_${index}`] ? 'error' : ''}
                                                autoComplete="off"
                                            />
                                            {loadingProducts && (
                                                <div className="search-loading-small">
                                                    <FontAwesomeIcon icon={faSpinner} spin />
                                                </div>
                                            )}

                                            {/* Product Dropdown */}
                                            {showProductDropdown[index] && filteredProducts.length > 0 && (
                                                <div className="product-dropdown-table">
                                                    {filteredProducts.map(product => (
                                                        <div
                                                            key={product.id}
                                                            className="product-option"
                                                            onClick={() => selectProduct(index, product)}
                                                        >
                                                            <div className="product-name">{product.product_name}</div>
                                                            <div className="product-details">
                                                                {product.product_colorcode && (
                                                                    <span><FontAwesomeIcon icon={faTag} /> Color: {product.product_colorcode}</span>
                                                                )}
                                                                {product.batch_no && (
                                                                    <span><FontAwesomeIcon icon={faTag} /> Batch: {product.batch_no}</span>
                                                                )}
                                                                {product.unit && (
                                                                    <span>Unit: {product.unit}</span>
                                                                )}
                                                                {(product.cost_price || product.sell_price) && (
                                                                    <span><FontAwesomeIcon icon={faRupeeSign} /> Price: {product.cost_price || product.sell_price}</span>
                                                                )}
                                                            </div>
                                                            <div className="product-stock">
                                                                Stock: {product.quantity}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}

                                            {showProductDropdown[index] && filteredProducts.length === 0 && productSearchTerms[index] && (
                                                <div className="product-dropdown-table empty">
                                                    <FontAwesomeIcon icon={faExclamationTriangle} />
                                                    {productType === 'fabric'
                                                        ? (translations?.noFabProductsFound || 'No fabric products found')
                                                        : (translations?.noProductsFound || 'No products found')}
                                                </div>
                                            )}
                                        </div>
                                        {errors[`product_name_${index}`] && (
                                            <div className="table-error">
                                                <FontAwesomeIcon icon={faExclamationTriangle} />
                                                {errors[`product_name_${index}`]}
                                            </div>
                                        )}
                                    </div>
                                </td>
                                
                                {/* Color Code Column - Only for fabric products */}
                                {productType === 'fabric' && (
                                    <td>
                                        <div className="table-field">
                                            <input
                                                type="text"
                                                value={item.product_colorcode || ''}
                                                onChange={(e) => {
                                                    const newValue = e.target.value;
                                                    onItemChange(index, 'product_colorcode', newValue);
                                                    setLocalItems(prev => {
                                                        const updated = [...prev];
                                                        if (!updated[index]) updated[index] = {};
                                                        updated[index] = { ...updated[index], product_colorcode: newValue };
                                                        return updated;
                                                    });
                                                }}
                                                placeholder="Color code"
                                                disabled={disabled}
                                                dir={isRTL ? 'rtl' : 'ltr'}
                                            />
                                        </div>
                                    </td>
                                )}
                                
                                <td>
                                    <div className="table-field">
                                        <input
                                            type="text"
                                            value={item.batch_no || ''}
                                            onChange={(e) => {
                                                const newValue = e.target.value;
                                                onItemChange(index, 'batch_no', newValue);
                                                setLocalItems(prev => {
                                                    const updated = [...prev];
                                                    if (!updated[index]) updated[index] = {};
                                                    updated[index] = { ...updated[index], batch_no: newValue };
                                                    return updated;
                                                });
                                            }}
                                            placeholder={translations?.batchNumber || 'Batch no'}
                                            disabled={disabled}
                                            dir={isRTL ? 'rtl' : 'ltr'}
                                            className={errors[`batch_no_${index}`] ? 'error' : ''}
                                        />
                                        {errors[`batch_no_${index}`] && (
                                            <div className="table-error">
                                                {errors[`batch_no_${index}`]}
                                            </div>
                                        )}
                                    </div>
                                </td>
                                <td>
                                    <div className="table-field">
                                        <input
                                            type="text"
                                            value={item.barcode || ''}
                                            onChange={(e) => {
                                                const newValue = e.target.value;
                                                onItemChange(index, 'barcode', newValue);
                                                setLocalItems(prev => {
                                                    const updated = [...prev];
                                                    if (!updated[index]) updated[index] = {};
                                                    updated[index] = { ...updated[index], barcode: newValue };
                                                    return updated;
                                                });
                                            }}
                                            placeholder="Barcode"
                                            disabled={disabled}
                                            dir={isRTL ? 'rtl' : 'ltr'}
                                        />
                                    </div>
                                </td>
                                <td>
                                    <div className="table-field">
                                        <select
                                            value={item.unit}
                                            onChange={(e) => {
                                                const newValue = e.target.value;
                                                onItemChange(index, 'unit', newValue);
                                                setLocalItems(prev => {
                                                    const updated = [...prev];
                                                    if (!updated[index]) updated[index] = {};
                                                    updated[index] = { ...updated[index], unit: newValue };
                                                    return updated;
                                                });
                                            }}
                                            disabled={disabled}
                                            dir={isRTL ? 'rtl' : 'ltr'}
                                            className={errors[`unit_${index}`] ? 'error' : ''}
                                        >
                                            <option value="">{translations?.selectUnit || 'Select'}</option>
                                            {units.map((unit) => (
                                                <option key={unit.value} value={unit.value}>
                                                    {unit.label}
                                                </option>
                                            ))}
                                        </select>
                                        {errors[`unit_${index}`] && (
                                            <div className="table-error">
                                                {errors[`unit_${index}`]}
                                            </div>
                                        )}
                                    </div>
                                </td>
                                <td>
                                    <div className="table-field">
                                        <input
                                            type="number"
                                            value={item.price}
                                            onChange={(e) => {
                                                const newValue = e.target.value;
                                                onItemChange(index, 'price', newValue);
                                                setLocalItems(prev => {
                                                    const updated = [...prev];
                                                    if (!updated[index]) updated[index] = {};
                                                    updated[index] = { ...updated[index], price: newValue };
                                                    return updated;
                                                });
                                            }}
                                            step="0.01"
                                            min="0"
                                            placeholder="0.00"
                                            disabled={disabled}
                                            dir="ltr"
                                            className={errors[`price_${index}`] ? 'error' : ''}
                                        />
                                        {errors[`price_${index}`] && (
                                            <div className="table-error">
                                                {errors[`price_${index}`]}
                                            </div>
                                        )}
                                    </div>
                                </td>
                                <td>
                                    <div className="table-field">
                                        <input
                                            type="number"
                                            value={item.quantity}
                                            onChange={(e) => {
                                                const newValue = e.target.value;
                                                onItemChange(index, 'quantity', newValue);
                                                setLocalItems(prev => {
                                                    const updated = [...prev];
                                                    if (!updated[index]) updated[index] = {};
                                                    updated[index] = { ...updated[index], quantity: newValue };
                                                    return updated;
                                                });
                                            }}
                                            step="0.001"
                                            min="0.001"
                                            placeholder="0.000"
                                            disabled={disabled}
                                            dir="ltr"
                                            className={errors[`quantity_${index}`] ? 'error' : ''}
                                        />
                                        {errors[`quantity_${index}`] && (
                                            <div className="table-error">
                                                {errors[`quantity_${index}`]}
                                            </div>
                                        )}
                                    </div>
                                </td>
                                <td>
                                    <div className="table-field">
                                        <input
                                            type="text"
                                            value={calculateTotal(item.price, item.quantity)}
                                            readOnly
                                            disabled
                                            className="readonly-input total-display"
                                        />
                                    </div>
                                </td>
                                <td>
                                    <button
                                        type="button"
                                        className="table-remove-btn"
                                        onClick={() => {
                                            onRemove(index);
                                            setLocalItems(prev => prev.filter((_, i) => i !== index));
                                        }}
                                        disabled={disabled || displayItems.length === 1}
                                        title={translations?.remove || 'Remove'}
                                    >
                                        <FontAwesomeIcon icon={faTrash} />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

// Main SupplierOrder Component
const SupplierOrder = ({ orderId = null, onSuccess = null, initialProductType = 'regular' }) => {
    const { user } = useAuth();

    // Get shop_id from user or localStorage
    const [shopId, setShopId] = useState(() => {
        if (user?.shop_id) {
            return user.shop_id;
        }
        return localStorage.getItem('current_shop_id') || '';
    });

    const [formData, setFormData] = useState({
        date: new Date().toISOString().split('T')[0],
        expected_delivery_date: '',
        order_number: '',
        supplierid: '',
        supplier_name: '',
        supplier_phone: '',
        supplier_email: '',
        supplier_address: '',
        supplier_company: '',
        order_file: null,
        subtotal: '0.00',
        tax: '0.00',
        discount: '0.00',
        grand_total: '0.00',
        paid_amount: '0.00',
        balance_amount: '0.00',
        payment_status: 'unpaid',
        payment_method: '',
        order_status: 'pending',
        notes: '',
        items: [
            {
                product_name: '',
                product_id: '',
                product_colorcode: '',
                batch_no: '',
                barcode: '',
                unit: '',
                price: '',
                quantity: ''
            }
        ],
        themeColor: localStorage.getItem('ourzest_theme') || '#8b5cf6'
    });

    const [suppliers, setSuppliers] = useState([]);
    const [filteredSuppliers, setFilteredSuppliers] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [showSupplierDropdown, setShowSupplierDropdown] = useState(false);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [currentLanguage, setCurrentLanguage] = useState(() => {
        return localStorage.getItem('ourzest_language') || 'en';
    });
    const [translations, setTranslations] = useState({});
    const [isTranslating, setIsTranslating] = useState(false);
    const [productType, setProductType] = useState(initialProductType); // 'regular' or 'fabric'

    // Toast state
    const [toast, setToast] = useState({
        show: false,
        type: '',
        message: '',
        direction: 'ltr',
        progress: 100,
        timeLeft: 3
    });

    // Refs
    const toastTimerRef = useRef(null);
    const supplierSearchRef = useRef(null);
    const fileInputRef = useRef(null);

    // Function to show toast
    const showToast = useCallback((type, message) => {
        if (toastTimerRef.current) {
            clearInterval(toastTimerRef.current);
        }

        const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
        const direction = isRTL ? 'rtl' : 'ltr';

        setToast({
            show: true,
            type,
            message,
            direction,
            progress: 100,
            timeLeft: 3
        });

        let timeLeft = 3;
        const timer = setInterval(() => {
            timeLeft -= 0.1;
            const progress = (timeLeft / 3) * 100;

            setToast(prev => ({
                ...prev,
                progress,
                timeLeft: parseFloat(timeLeft.toFixed(1))
            }));

            if (timeLeft <= 0) {
                clearInterval(timer);
                setToast(prev => ({ ...prev, show: false }));
            }
        }, 100);

        toastTimerRef.current = timer;
    }, [currentLanguage]);

    // Clean up timer on component unmount
    useEffect(() => {
        return () => {
            if (toastTimerRef.current) {
                clearInterval(toastTimerRef.current);
            }
        };
    }, []);

    // Load translations
    const loadTranslations = useCallback(async (lang) => {
        if (!lang) return;

        setIsTranslating(true);
        try {
            const trans = await supplierOrderTranslator.getTranslations(lang);
            setTranslations(trans);

            const isRTL = lang === 'ur' || lang === 'ar';
            document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
            document.body.dir = isRTL ? 'rtl' : 'ltr';

        } catch (error) {
            console.error('Error loading translations:', error);
        } finally {
            setIsTranslating(false);
        }
    }, []);

    // Setup language change listener
    useEffect(() => {
        loadTranslations(currentLanguage);

        const handleLanguageChange = () => {
            const newLang = localStorage.getItem('ourzest_language') || 'en';
            if (newLang !== currentLanguage) {
                setCurrentLanguage(newLang);
                loadTranslations(newLang);
            }
        };

        window.addEventListener('storage', handleLanguageChange);

        const intervalId = setInterval(() => {
            const storedLang = localStorage.getItem('ourzest_language') || 'en';
            if (storedLang !== currentLanguage) {
                setCurrentLanguage(storedLang);
                loadTranslations(storedLang);
            }
        }, 500);

        const translationListener = (lang) => {
            if (lang !== currentLanguage) {
                setCurrentLanguage(lang);
                loadTranslations(lang);
            }
        };

        supplierOrderTranslator.addListener(translationListener);

        return () => {
            window.removeEventListener('storage', handleLanguageChange);
            clearInterval(intervalId);
            supplierOrderTranslator.removeListener(translationListener);
        };
    }, [currentLanguage, loadTranslations]);

    // Apply theme
    useEffect(() => {
        const applyTheme = (color) => {
            try {
                if (!/^#[0-9A-F]{6}$/i.test(color)) color = '#8b5cf6';

                const hex = color.replace('#', '');
                const r = parseInt(hex.substring(0, 2), 16);
                const g = parseInt(hex.substring(2, 4), 16);
                const b = parseInt(hex.substring(4, 6), 16);

                const adjust = (amount) => {
                    let newR = Math.max(0, Math.min(255, r + amount));
                    let newG = Math.max(0, Math.min(255, g + amount));
                    let newB = Math.max(0, Math.min(255, b + amount));
                    return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
                };

                const palette = {
                    'supplierorder-primary-color': color,
                    'supplierorder-primary-dark': adjust(-40),
                    'supplierorder-primary-light': adjust(40),
                    'supplierorder-primary-ultralight': adjust(20),
                    'supplierorder-primary-gradient': `linear-gradient(135deg, ${color}, ${adjust(-20)})`,
                    'supplierorder-primary-shadow': `${color}20`
                };

                Object.entries(palette).forEach(([key, value]) => {
                    document.documentElement.style.setProperty(`--${key}`, value);
                });
            } catch (err) {
                console.error("Error applying theme:", err);
            }
        };

        applyTheme(formData.themeColor);

        const handleThemeChange = () => {
            const theme = localStorage.getItem('ourzest_theme') || '#8b5cf6';
            if (theme !== formData.themeColor) {
                setFormData(prev => ({ ...prev, themeColor: theme }));
                applyTheme(theme);
            }
        };

        handleThemeChange();
        window.addEventListener('storage', handleThemeChange);
        const interval = setInterval(handleThemeChange, 1000);

        return () => {
            window.removeEventListener('storage', handleThemeChange);
            clearInterval(interval);
        };
    }, [formData.themeColor]);

    // Load suppliers
    const loadSuppliers = async () => {
        if (!user?.username || !shopId) return;

        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/get_suppliers.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    username: user?.username,
                    shop_id: shopId,
                    searchTerm: '',
                    page: 1,
                    limit: 50
                })
            });

            const data = await response.json();

            if (data.status === 'success') {
                setSuppliers(data.data);
                setFilteredSuppliers(data.data);
            } else {
                showToast('error', data.message || translations?.failedLoadSuppliers || 'Failed to load suppliers');
            }
        } catch (error) {
            console.error('Error loading suppliers:', error);
            showToast('error', translations?.networkError || 'Network error occurred');
        }
    };

    // Load order data if in edit mode
    useEffect(() => {
        if (orderId) {
            setIsEditMode(true);
            loadOrderData(orderId);
        }
    }, [orderId]);

    // Calculate totals when items change
    useEffect(() => {
        calculateTotals();
    }, [formData.items, formData.tax, formData.discount]);

    // Handle click outside supplier dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (supplierSearchRef.current && !supplierSearchRef.current.contains(event.target)) {
                setShowSupplierDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        loadSuppliers();
    }, [user?.username, shopId]);

    const handleSupplierSearch = async (term) => {
        setSearchTerm(term);
        setShowSupplierDropdown(true);

        if (!user?.username || !shopId) return;

        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/get_suppliers.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    username: user?.username,
                    shop_id: shopId,
                    searchTerm: term,
                    page: 1,
                    limit: 50
                })
            });

            const data = await response.json();

            if (data.status === 'success') {
                setFilteredSuppliers(data.data);
            }
        } catch (error) {
            console.error('Error searching suppliers:', error);
        }
    };

    const selectSupplier = (supplier) => {
        setFormData(prev => ({
            ...prev,
            supplierid: supplier.supplierid,
            supplier_name: supplier.supplier_name || '',
            supplier_phone: supplier.phone || '',
            supplier_email: supplier.email || '',
            supplier_address: supplier.address || '',
            supplier_company: supplier.company || ''
        }));
        setSearchTerm(supplier.supplier_name || '');
        setShowSupplierDropdown(false);

        // Clear supplier error if exists
        if (errors.supplier) {
            setErrors(prev => ({ ...prev, supplier: '' }));
        }

        showToast('success', `Supplier "${supplier.supplier_name}" selected`);
    };

    const loadOrderData = async (id) => {
        setLoading(true);
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/supplierorder.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    action: 'get_record',
                    id: id,
                    username: user?.username,
                    shop_id: shopId
                })
            });

            const data = await response.json();

            if (data.status === 'success' && data.data) {
                const order = data.data;
                setFormData({
                    date: order.date || new Date().toISOString().split('T')[0],
                    expected_delivery_date: order.expected_delivery_date || '',
                    order_number: order.order_number || '',
                    supplierid: order.supplierid || '',
                    supplier_name: order.supplier_name || '',
                    supplier_phone: order.supplier_phone || '',
                    supplier_email: order.supplier_email || '',
                    supplier_address: order.supplier_address || '',
                    supplier_company: order.supplier_company || '',
                    order_file: null,
                    subtotal: order.subtotal || '0.00',
                    tax: order.tax || '0.00',
                    discount: order.discount || '0.00',
                    grand_total: order.grand_total || '0.00',
                    paid_amount: order.paid_amount || '0.00',
                    balance_amount: order.balance_amount || '0.00',
                    payment_status: order.payment_status || 'unpaid',
                    payment_method: order.payment_method || '',
                    order_status: order.order_status || 'pending',
                    notes: order.notes || '',
                    items: order.items || [{
                        product_name: '',
                        product_id: '',
                        product_colorcode: '',
                        batch_no: '',
                        barcode: '',
                        unit: '',
                        price: '',
                        quantity: ''
                    }],
                    themeColor: formData.themeColor
                });

                if (order.supplier_name) {
                    setSearchTerm(order.supplier_name);
                }
            } else {
                showToast('error', data.message || 'Failed to load order data');
            }
        } catch (error) {
            showToast('error', 'Network error occurred');
        } finally {
            setLoading(false);
        }
    };

    const handleItemChange = (index, field, value) => {
        const updatedItems = [...formData.items];
        updatedItems[index] = {
            ...updatedItems[index],
            [field]: value
        };

        setFormData(prev => ({
            ...prev,
            items: updatedItems
        }));

        // Clear error for this field
        const errorKey = `${field}_${index}`;
        if (errors[errorKey]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[errorKey];
                return newErrors;
            });
        }
    };

    const addItem = () => {
        setFormData(prev => ({
            ...prev,
            items: [
                ...prev.items,
                {
                    product_name: '',
                    product_id: '',
                    product_colorcode: '',
                    batch_no: '',
                    barcode: '',
                    unit: '',
                    price: '',
                    quantity: ''
                }
            ]
        }));
    };

    const removeItem = (index) => {
        if (formData.items.length > 1) {
            const updatedItems = [...formData.items];
            updatedItems.splice(index, 1);
            setFormData(prev => ({
                ...prev,
                items: updatedItems
            }));

            // Clear errors for removed item
            const fields = ['product_name', 'batch_no', 'unit', 'price', 'quantity'];
            setErrors(prev => {
                const newErrors = { ...prev };
                fields.forEach(field => {
                    delete newErrors[`${field}_${index}`];
                });
                return newErrors;
            });
        }
    };

    const calculateTotals = () => {
        let subtotal = 0;

        formData.items.forEach(item => {
            const price = parseFloat(item.price) || 0;
            const quantity = parseFloat(item.quantity) || 0;
            subtotal += price * quantity;
        });

        const tax = parseFloat(formData.tax) || 0;
        const discount = parseFloat(formData.discount) || 0;
        const grandTotal = subtotal + tax - discount;
        const paidAmount = parseFloat(formData.paid_amount) || 0;
        const balance = grandTotal - paidAmount;

        setFormData(prev => ({
            ...prev,
            subtotal: subtotal.toFixed(2),
            grand_total: grandTotal.toFixed(2),
            balance_amount: balance.toFixed(2)
        }));
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) {
                showToast('error', 'File size must be less than 5MB');
                return;
            }

            const reader = new FileReader();
            reader.onload = (event) => {
                setFormData(prev => ({
                    ...prev,
                    order_file: {
                        name: file.name,
                        type: file.type,
                        data: event.target.result.split(',')[1]
                    }
                }));
            };
            reader.readAsDataURL(file);
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.order_number?.trim()) {
            newErrors.order_number = translations?.orderRequired || 'Order number is required';
        }

        if (!formData.supplierid) {
            newErrors.supplier = translations?.supplierRequired || 'Supplier is required';
        }

        if (!formData.date) {
            newErrors.date = translations?.dateRequired || 'Date is required';
        }

        if (!formData.items || formData.items.length === 0) {
            newErrors.items = translations?.itemsRequired || 'At least one item is required';
        } else {
            formData.items.forEach((item, index) => {
                // Check for product name
                if (!item.product_name?.trim()) {
                    newErrors[`product_name_${index}`] = translations?.productRequired || 'Product name is required';
                }

                // Check for price
                if (!item.price || parseFloat(item.price) <= 0) {
                    newErrors[`price_${index}`] = translations?.priceRequired || 'Price is required';
                }

                // Check for quantity
                if (!item.quantity || parseFloat(item.quantity) <= 0) {
                    newErrors[`quantity_${index}`] = translations?.quantityRequired || 'Quantity is required';
                }
            });
        }

        const paidAmount = parseFloat(formData.paid_amount);
        if (isNaN(paidAmount) || paidAmount < 0) {
            newErrors.paid_amount = translations?.invalidPaid || 'Please enter a valid paid amount';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }

        // Recalculate totals when tax or discount changes
        if (name === 'tax' || name === 'discount' || name === 'paid_amount') {
            setTimeout(() => calculateTotals(), 0);
        }
    };

    const generateOrderNumber = async () => {
        if (!formData.order_number && shopId) {
            try {
                const response = await fetch('https://ourzestapp.online/oz_backend/supplierorder.php', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        action: 'generate_order_number',
                        shop_id: shopId
                    })
                });

                const data = await response.json();

                if (data.status === 'success') {
                    setFormData(prev => ({
                        ...prev,
                        order_number: data.data.order_number
                    }));
                }
            } catch (error) {
                console.error('Error generating order number:', error);
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            const firstError = document.querySelector('.has-error, .error-message-modern');
            if (firstError) {
                firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            return;
        }

        setSaving(true);

        try {
            const currentUsername = user?.username || 'Unknown';
            const currentShopId = shopId || localStorage.getItem('current_shop_id');

            if (!currentUsername || currentUsername === 'Unknown') {
                throw new Error("User not authenticated");
            }

            if (!currentShopId) {
                throw new Error("Shop ID is required. Please select a shop first.");
            }

            const action = isEditMode ? 'edit' : 'add';
            const endpoint = 'https://ourzestapp.online/oz_backend/supplierorder.php';

            // Process items
            const processedItems = formData.items.map(item => ({
                product_name: item.product_name || '',
                product_id: item.product_id || null,
                product_colorcode: item.product_colorcode || '',
                batch_no: item.batch_no || '',
                barcode: item.barcode || '',
                unit: item.unit || '',
                price: parseFloat(item.price || 0).toFixed(2),
                quantity: parseFloat(item.quantity || 0).toFixed(3),
                total: ((parseFloat(item.price) || 0) * (parseFloat(item.quantity) || 0)).toFixed(2)
            }));

            // Create payload
            const payload = {
                action: action,
                username: currentUsername,
                shop_id: parseInt(currentShopId),
                date: formData.date,
                expected_delivery_date: formData.expected_delivery_date || null,
                order_number: formData.order_number,
                supplierid: parseInt(formData.supplierid) || 0,
                supplier_name: formData.supplier_name,
                supplier_phone: formData.supplier_phone || '',
                supplier_email: formData.supplier_email || '',
                supplier_address: formData.supplier_address || '',
                supplier_company: formData.supplier_company || '',
                subtotal: parseFloat(formData.subtotal || 0).toFixed(2),
                tax: parseFloat(formData.tax || 0).toFixed(2),
                discount: parseFloat(formData.discount || 0).toFixed(2),
                grand_total: parseFloat(formData.grand_total || 0).toFixed(2),
                paid_amount: parseFloat(formData.paid_amount || 0).toFixed(2),
                payment_status: formData.payment_status,
                payment_method: formData.payment_method,
                order_status: formData.order_status,
                notes: formData.notes || '',
                items: processedItems
            };

            // Add order_file if exists
            if (formData.order_file) {
                payload.order_file = JSON.stringify(formData.order_file);
            }

            // Add ID for edit mode
            if (isEditMode && orderId) {
                payload.id = orderId;
            }

            console.log('Submitting payload:', payload);

            const response = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload)
            });

            const responseText = await response.text();
            console.log('Raw response:', responseText);

            let data;
            try {
                data = JSON.parse(responseText);
            } catch (parseError) {
                console.error('Failed to parse response as JSON:', parseError);
                throw new Error(`Server returned invalid JSON: ${responseText.substring(0, 200)}`);
            }

            if (data.status === 'success') {
                const successMessage = isEditMode
                    ? translations?.orderUpdated || 'Order updated successfully!'
                    : translations?.orderSaved || 'Order saved successfully!';

                showToast('success', successMessage);

                if (!isEditMode) {
                    // Reset form after successful save
                    setFormData({
                        date: new Date().toISOString().split('T')[0],
                        expected_delivery_date: '',
                        order_number: '',
                        supplierid: '',
                        supplier_name: '',
                        supplier_phone: '',
                        supplier_email: '',
                        supplier_address: '',
                        supplier_company: '',
                        order_file: null,
                        subtotal: '0.00',
                        tax: '0.00',
                        discount: '0.00',
                        grand_total: '0.00',
                        paid_amount: '0.00',
                        balance_amount: '0.00',
                        payment_status: 'unpaid',
                        payment_method: '',
                        order_status: 'pending',
                        notes: '',
                        items: [{
                            product_name: '',
                            product_id: '',
                            product_colorcode: '',
                            batch_no: '',
                            barcode: '',
                            unit: '',
                            price: '',
                            quantity: ''
                        }],
                        themeColor: formData.themeColor
                    });
                    setErrors({});
                    setSearchTerm('');
                }

                if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                }

                if (onSuccess) {
                    onSuccess(data.order_id || orderId);
                }
            } else {
                const errorMessage = data.message ||
                    (isEditMode
                        ? translations?.failedUpdate || 'Failed to update order'
                        : translations?.failedSave || 'Failed to save order');
                showToast('error', errorMessage);
            }
        } catch (error) {
            console.error('Submission error:', error);
            showToast('error', error.message || translations?.networkError || 'Network error occurred');
        } finally {
            setSaving(false);
        }
    };

    const handleClear = () => {
        setFormData({
            date: new Date().toISOString().split('T')[0],
            expected_delivery_date: '',
            order_number: '',
            supplierid: '',
            supplier_name: '',
            supplier_phone: '',
            supplier_email: '',
            supplier_address: '',
            supplier_company: '',
            order_file: null,
            subtotal: '0.00',
            tax: '0.00',
            discount: '0.00',
            grand_total: '0.00',
            paid_amount: '0.00',
            balance_amount: '0.00',
            payment_status: 'unpaid',
            payment_method: '',
            order_status: 'pending',
            notes: '',
            items: [{
                product_name: '',
                product_id: '',
                product_colorcode: '',
                batch_no: '',
                barcode: '',
                unit: '',
                price: '',
                quantity: ''
            }],
            themeColor: formData.themeColor
        });
        setErrors({});
        setSearchTerm('');
        setIsEditMode(false);

        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const toggleProductType = () => {
        setProductType(prev => prev === 'regular' ? 'fabric' : 'regular');
    };

    const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
    const subtotal = formData.items.reduce((sum, item) => {
        const price = parseFloat(item.price) || 0;
        const quantity = parseFloat(item.quantity) || 0;
        return sum + (price * quantity);
    }, 0);
    const tax = parseFloat(formData.tax) || 0;
    const discount = parseFloat(formData.discount) || 0;
    const grandTotal = subtotal + tax - discount;

    // Payment status options
    const paymentStatusOptions = [
        { value: 'unpaid', label: translations?.unpaid || 'Unpaid' },
        { value: 'partial', label: translations?.partial || 'Partial' },
        { value: 'paid', label: translations?.paid || 'Paid' }
    ];

    // Order status options
    const orderStatusOptions = [
        { value: 'pending', label: translations?.pending || 'Pending' },
        { value: 'processing', label: translations?.processing || 'Processing' },
        { value: 'completed', label: translations?.completed || 'Completed' },
        { value: 'cancelled', label: translations?.cancelled || 'Cancelled' }
    ];

    // Payment method options
    const paymentMethodOptions = [
        { value: 'cash', label: translations?.cash || 'Cash' },
        { value: 'card', label: translations?.card || 'Card' },
        { value: 'bank_transfer', label: translations?.bankTransfer || 'Bank Transfer' },
        { value: 'cheque', label: translations?.cheque || 'Cheque' },
        { value: 'credit', label: translations?.credit || 'Credit' }
    ];

    return (
        <div className={`supplierorder-form-independent ${isRTL ? 'rtl' : ''} ${isTranslating ? 'translating' : ''}`}>
            {/* Toast Notification */}
            <ToastNotification
                toast={toast}
                setToast={setToast}
                translations={translations}
            />

            {/* Modern Form Header */}
            <div className="form-header-modern">
                <div className="header-content">
                    <div className="header-icon">
                        <FontAwesomeIcon icon={isEditMode ? faShoppingCart : faPlus} />
                    </div>
                    <div className="header-text">
                        <h1>{isEditMode ? (translations?.editOrder || 'Edit Supplier Order') : (translations?.addOrder || 'Add Supplier Order')}</h1>
                        <p>{translations?.orderDetails || 'Supplier Order Details'}</p>
                    </div>
                </div>
                {isTranslating && (
                    <div className="translation-indicator">
                        <FontAwesomeIcon icon={faSpinner} spin />
                        <span>Translating...</span>
                    </div>
                )}
            </div>

            {/* Product Type Toggle */}
            <div className="product-type-toggle">
                <button
                    type="button"
                    className={`toggle-btn ${productType === 'regular' ? 'active' : ''}`}
                    onClick={() => setProductType('regular')}
                >
                    <FontAwesomeIcon icon={faBox} />
                    {translations?.regularProducts || 'Regular Products'}
                </button>
                <button
                    type="button"
                    className={`toggle-btn ${productType === 'fabric' ? 'active' : ''}`}
                    onClick={() => setProductType('fabric')}
                >
                    <FontAwesomeIcon icon={faIndustry} />
                    {translations?.fabricProducts || 'Fabric Products'}
                </button>
            </div>

            {/* Modern Form */}
            <form onSubmit={handleSubmit} className="modern-form">
                {/* Basic Information Section */}
                <div className="form-section">
                    <h3 className="section-title">
                        <FontAwesomeIcon icon={faReceipt} />
                        {translations?.orderDetails || 'Order Information'}
                    </h3>

                    <div className="form-grid-modern">
                        {/* Date Field */}
                        <div className={`form-group-modern ${errors.date ? 'has-error' : ''}`}>
                            <div className="input-container">
                                <FontAwesomeIcon icon={faCalendarAlt} className="input-icon" />
                                <input
                                    type="date"
                                    id="date"
                                    name="date"
                                    value={formData.date}
                                    onChange={handleChange}
                                    disabled={saving}
                                    dir="ltr"
                                />
                                <label htmlFor="date" className="input-label">
                                    {translations?.date || 'Order Date'}
                                    <span className="required">*</span>
                                </label>
                            </div>
                            {errors.date && (
                                <div className="error-message-modern">
                                    <FontAwesomeIcon icon={faExclamationTriangle} />
                                    {errors.date}
                                </div>
                            )}
                        </div>

                        {/* Expected Delivery Date */}
                        <div className="form-group-modern">
                            <div className="input-container">
                                <FontAwesomeIcon icon={faTruck} className="input-icon" />
                                <input
                                    type="date"
                                    id="expected_delivery_date"
                                    name="expected_delivery_date"
                                    value={formData.expected_delivery_date}
                                    onChange={handleChange}
                                    disabled={saving}
                                    dir="ltr"
                                />
                                <label htmlFor="expected_delivery_date" className="input-label">
                                    {translations?.expectedDelivery || 'Expected Delivery'}
                                </label>
                            </div>
                        </div>

                        {/* Order Number Field */}
                        <div className={`form-group-modern ${errors.order_number ? 'has-error' : ''}`}>
                            <div className="input-container">
                                <FontAwesomeIcon icon={faFileInvoice} className="input-icon" />
                                <input
                                    type="text"
                                    id="order_number"
                                    name="order_number"
                                    value={formData.order_number}
                                    onChange={handleChange}
                                    required
                                    placeholder={translations?.orderNumber || 'Enter order number'}
                                    disabled={saving}
                                    dir={isRTL ? 'rtl' : 'ltr'}
                                />
                                <label htmlFor="order_number" className="input-label">
                                    {translations?.orderNumber || 'Order Number'}
                                    <span className="required">*</span>
                                </label>
                                <button
                                    type="button"
                                    className="generate-btn"
                                    onClick={generateOrderNumber}
                                    disabled={saving || !!formData.order_number}
                                    title={translations?.generate || 'Generate'}
                                >
                                    <FontAwesomeIcon icon={faPlus} />
                                </button>
                            </div>
                            {errors.order_number && (
                                <div className="error-message-modern">
                                    <FontAwesomeIcon icon={faExclamationTriangle} />
                                    {errors.order_number}
                                </div>
                            )}
                        </div>

                        {/* Supplier Search Field */}
                        <div className={`form-group-modern full-width ${errors.supplier ? 'has-error' : ''}`} ref={supplierSearchRef}>
                            <div className="input-container">
                                <FontAwesomeIcon icon={faSearch} className="input-icon search-icon" />
                                <input
                                    type="text"
                                    id="supplier_search"
                                    value={searchTerm}
                                    onChange={(e) => handleSupplierSearch(e.target.value)}
                                    onFocus={() => setShowSupplierDropdown(true)}
                                    placeholder={translations?.searchSupplier || 'Search supplier...'}
                                    disabled={saving}
                                    dir={isRTL ? 'rtl' : 'ltr'}
                                    autoComplete="off"
                                />
                                <label htmlFor="supplier_search" className="input-label">
                                    {translations?.supplier || 'Supplier'}
                                    <span className="required">*</span>
                                </label>
                                <FontAwesomeIcon icon={faChevronDown} className="dropdown-icon" />
                            </div>

                            {/* Supplier Dropdown */}
                            {showSupplierDropdown && (
                                <div className="supplier-dropdown">
                                    {filteredSuppliers.length > 0 ? (
                                        filteredSuppliers.map(supplier => (
                                            <div
                                                key={supplier.supplierid}
                                                className="supplier-option"
                                                onClick={() => selectSupplier(supplier)}
                                            >
                                                <div className="supplier-name">{supplier.supplier_name}</div>
                                                <div className="supplier-info">
                                                    {supplier.phone && <span><FontAwesomeIcon icon={faPhone} /> {supplier.phone}</span>}
                                                    {supplier.email && <span><FontAwesomeIcon icon={faEnvelope} /> {supplier.email}</span>}
                                                    {supplier.company && <span><FontAwesomeIcon icon={faBuilding} /> {supplier.company}</span>}
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="supplier-dropdown empty">
                                            <FontAwesomeIcon icon={faExclamationTriangle} />
                                            {translations?.noSuppliersFound || 'No suppliers found'}
                                        </div>
                                    )}
                                </div>
                            )}

                            {errors.supplier && (
                                <div className="error-message-modern">
                                    <FontAwesomeIcon icon={faExclamationTriangle} />
                                    {errors.supplier}
                                </div>
                            )}
                        </div>

                        {/* Supplier Name (Read-only) */}
                        <div className="form-group-modern">
                            <div className="input-container">
                                <FontAwesomeIcon icon={faUser} className="input-icon" />
                                <input
                                    type="text"
                                    id="supplier_name"
                                    name="supplier_name"
                                    value={formData.supplier_name}
                                    readOnly
                                    placeholder={translations?.supplierName || 'Supplier name will auto-fill'}
                                    disabled
                                    dir={isRTL ? 'rtl' : 'ltr'}
                                    className="readonly-input"
                                />
                                <label htmlFor="supplier_name" className="input-label">
                                    {translations?.supplierName || 'Supplier Name'}
                                </label>
                            </div>
                        </div>

                        {/* Supplier Phone */}
                        <div className="form-group-modern">
                            <div className="input-container">
                                <FontAwesomeIcon icon={faPhone} className="input-icon" />
                                <input
                                    type="tel"
                                    id="supplier_phone"
                                    name="supplier_phone"
                                    value={formData.supplier_phone}
                                    readOnly
                                    placeholder={translations?.phone || 'Phone'}
                                    disabled
                                    dir="ltr"
                                    className="readonly-input"
                                />
                                <label htmlFor="supplier_phone" className="input-label">
                                    {translations?.phone || 'Phone'}
                                </label>
                            </div>
                        </div>

                        {/* Supplier Email */}
                        <div className="form-group-modern">
                            <div className="input-container">
                                <FontAwesomeIcon icon={faEnvelope} className="input-icon" />
                                <input
                                    type="email"
                                    id="supplier_email"
                                    name="supplier_email"
                                    value={formData.supplier_email}
                                    readOnly
                                    placeholder={translations?.email || 'Email'}
                                    disabled
                                    dir="ltr"
                                    className="readonly-input"
                                />
                                <label htmlFor="supplier_email" className="input-label">
                                    {translations?.email || 'Email'}
                                </label>
                            </div>
                        </div>

                        {/* Supplier Company */}
                        <div className="form-group-modern">
                            <div className="input-container">
                                <FontAwesomeIcon icon={faBuilding} className="input-icon" />
                                <input
                                    type="text"
                                    id="supplier_company"
                                    name="supplier_company"
                                    value={formData.supplier_company}
                                    readOnly
                                    placeholder={translations?.company || 'Company'}
                                    disabled
                                    dir={isRTL ? 'rtl' : 'ltr'}
                                    className="readonly-input"
                                />
                                <label htmlFor="supplier_company" className="input-label">
                                    {translations?.company || 'Company'}
                                </label>
                            </div>
                        </div>

                        {/* Supplier Address */}
                        <div className="form-group-modern full-width">
                            <div className="input-container">
                                <FontAwesomeIcon icon={faMapMarkerAlt} className="input-icon" />
                                <input
                                    type="text"
                                    id="supplier_address"
                                    name="supplier_address"
                                    value={formData.supplier_address}
                                    readOnly
                                    placeholder={translations?.address || 'Address'}
                                    disabled
                                    dir={isRTL ? 'rtl' : 'ltr'}
                                    className="readonly-input"
                                />
                                <label htmlFor="supplier_address" className="input-label">
                                    {translations?.address || 'Address'}
                                </label>
                            </div>
                        </div>

                        {/* File Upload Field */}
                        <div className="form-group-modern full-width">
                            <div className="input-container file-upload-container">
                                <FontAwesomeIcon icon={faUpload} className="input-icon" />
                                <input
                                    type="file"
                                    id="order_file"
                                    ref={fileInputRef}
                                    onChange={handleFileChange}
                                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                                    disabled={saving}
                                    className="file-input"
                                />
                                <label htmlFor="order_file" className="file-upload-label">
                                    <span className="file-text">
                                        {formData.order_file
                                            ? formData.order_file.name
                                            : translations?.chooseFile || 'Choose file'
                                        }
                                    </span>
                                    <span className="file-browse">
                                        <FontAwesomeIcon icon={faUpload} />
                                        {translations?.browse || 'Browse'}
                                    </span>
                                </label>
                                <label className="input-label">
                                    {translations?.orderFile || 'Order File'}
                                    <span className="optional">{translations?.optional ? ` (${translations.optional})` : ' (Optional)'}</span>
                                </label>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Items Section */}
                <div className="form-section">
                    <OrderItemsTable
                        items={formData.items}
                        onItemChange={handleItemChange}
                        onRemove={removeItem}
                        onAdd={addItem}
                        errors={errors}
                        disabled={saving}
                        translations={translations}
                        isRTL={isRTL}
                        user={user}
                        shopId={shopId}
                        showToast={showToast}
                        productType={productType}
                    />
                </div>

                {/* Calculations Section */}
                <div className="form-section">
                    <h3 className="section-title">
                        <FontAwesomeIcon icon={faCalculator} />
                        {translations?.calculations || 'Calculations'}
                    </h3>

                    <div className="calculations-grid">
                        {/* Subtotal */}
                        <div className="calculation-row">
                            <span className="calculation-label">{translations?.subtotal || 'Subtotal'}:</span>
                            <span className="calculation-value">Rs {subtotal.toFixed(2)}</span>
                        </div>

                        {/* Tax Field */}
                        <div className="calculation-row">
                            <span className="calculation-label">{translations?.tax || 'Tax'}:</span>
                            <div className="calculation-input-wrapper">
                                <input
                                    type="number"
                                    id="tax"
                                    name="tax"
                                    value={formData.tax}
                                    onChange={handleChange}
                                    step="0.01"
                                    min="0"
                                    placeholder="0.00"
                                    disabled={saving}
                                    dir="ltr"
                                />
                            </div>
                        </div>

                        {/* Discount Field */}
                        <div className="calculation-row">
                            <span className="calculation-label">{translations?.discount || 'Discount'}:</span>
                            <div className="calculation-input-wrapper">
                                <input
                                    type="number"
                                    id="discount"
                                    name="discount"
                                    value={formData.discount}
                                    onChange={handleChange}
                                    step="0.01"
                                    min="0"
                                    placeholder="0.00"
                                    disabled={saving}
                                    dir="ltr"
                                />
                            </div>
                        </div>

                        {/* Grand Total */}
                        <div className="calculation-row grand-total">
                            <span className="calculation-label">{translations?.grandTotal || 'Grand Total'}:</span>
                            <span className="calculation-value">Rs {grandTotal.toFixed(2)}</span>
                        </div>

                        {/* Paid Amount Field */}
                        <div className={`calculation-row paid-field ${errors.paid_amount ? 'has-error' : ''}`}>
                            <span className="calculation-label">{translations?.paidAmount || 'Paid Amount'}:</span>
                            <div className="paid-input-wrapper">
                                <input
                                    type="number"
                                    id="paid_amount"
                                    name="paid_amount"
                                    value={formData.paid_amount}
                                    onChange={handleChange}
                                    required
                                    step="0.01"
                                    min="0"
                                    placeholder="0.00"
                                    disabled={saving}
                                    dir="ltr"
                                    className={errors.paid_amount ? 'error' : ''}
                                />
                            </div>
                        </div>
                        {errors.paid_amount && (
                            <div className="error-message-modern full-width">
                                <FontAwesomeIcon icon={faExclamationTriangle} />
                                {errors.paid_amount}
                            </div>
                        )}

                        {/* Balance Display */}
                        <div className="calculation-row balance">
                            <span className="calculation-label">{translations?.balance || 'Balance'}:</span>
                            <span className={`calculation-value ${parseFloat(formData.balance_amount) > 0 ? 'negative' : 'positive'}`}>
                                Rs {formData.balance_amount}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Status and Payment Section */}
                <div className="form-section">
                    <h3 className="section-title">
                        <FontAwesomeIcon icon={faClock} />
                        {translations?.orderStatus || 'Status & Payment'}
                    </h3>

                    <div className="status-grid">
                        {/* Payment Status */}
                        <div className="form-group-modern">
                            <div className="input-container">
                                <FontAwesomeIcon icon={faMoneyBillWave} className="input-icon" />
                                <select
                                    id="payment_status"
                                    name="payment_status"
                                    value={formData.payment_status}
                                    onChange={handleChange}
                                    disabled={saving}
                                    dir={isRTL ? 'rtl' : 'ltr'}
                                >
                                    {paymentStatusOptions.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                                <label htmlFor="payment_status" className="input-label">
                                    {translations?.paymentStatus || 'Payment Status'}
                                </label>
                            </div>
                        </div>

                        {/* Payment Method */}
                        <div className="form-group-modern">
                            <div className="input-container">
                                <FontAwesomeIcon icon={faCreditCard} className="input-icon" />
                                <select
                                    id="payment_method"
                                    name="payment_method"
                                    value={formData.payment_method}
                                    onChange={handleChange}
                                    disabled={saving}
                                    dir={isRTL ? 'rtl' : 'ltr'}
                                >
                                    <option value="">{translations?.selectPaymentMethod || 'Select payment method'}</option>
                                    {paymentMethodOptions.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                                <label htmlFor="payment_method" className="input-label">
                                    {translations?.paymentMethod || 'Payment Method'}
                                </label>
                            </div>
                        </div>

                        {/* Order Status */}
                        <div className="form-group-modern">
                            <div className="input-container">
                                <FontAwesomeIcon icon={faClock} className="input-icon" />
                                <select
                                    id="order_status"
                                    name="order_status"
                                    value={formData.order_status}
                                    onChange={handleChange}
                                    disabled={saving}
                                    dir={isRTL ? 'rtl' : 'ltr'}
                                >
                                    {orderStatusOptions.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                                <label htmlFor="order_status" className="input-label">
                                    {translations?.orderStatus || 'Order Status'}
                                </label>
                            </div>
                        </div>

                        {/* Notes */}
                        <div className="form-group-modern full-width">
                            <div className="input-container">
                                <FontAwesomeIcon icon={faStickyNote} className="input-icon" />
                                <textarea
                                    id="notes"
                                    name="notes"
                                    value={formData.notes}
                                    onChange={handleChange}
                                    placeholder={translations?.notes || 'Enter any additional notes...'}
                                    disabled={saving}
                                    dir={isRTL ? 'rtl' : 'ltr'}
                                    rows="3"
                                />
                                <label htmlFor="notes" className="input-label">
                                    {translations?.notes || 'Notes'}
                                </label>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Form Actions */}
                <div className="form-actions-modern">
                    <button
                        type="button"
                        className="btn-modern btn-secondary-modern"
                        onClick={handleClear}
                        disabled={saving}
                    >
                        <FontAwesomeIcon icon={faTimes} />
                        <span>{translations?.clear || 'Clear'}</span>
                    </button>
                    <button
                        type="submit"
                        className="btn-modern btn-primary-modern"
                        disabled={saving || isTranslating}
                    >
                        {saving ? (
                            <>
                                <div className="btn-spinner"></div>
                                <span>{translations?.saving || 'Saving...'}</span>
                            </>
                        ) : (
                            <>
                                <span>{isEditMode ? (translations?.updateOrder || 'Update') : (translations?.saveOrder || 'Save')}</span>
                                <FontAwesomeIcon icon={faSave} />
                            </>
                        )}
                    </button>
                </div>
            </form>

            {/* Loading Overlay */}
            {loading && (
                <div className="loading-overlay">
                    <div className="loading-content">
                        <div className="loading-spinner"></div>
                        <p>{translations?.saving || 'Loading...'}</p>
                        <p className="loading-subtext">Please wait while we load order data</p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SupplierOrder;